import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircleIcon, PlusIcon, Trash2Icon } from 'lucide-react'
import {
	Alert, AlertDescription, AlertTitle,
	Button, ConfirmDialog, IconButton, Spinner, Textarea,
} from '@rentflowdev/ui'
import {
	useCreateDealNoteMutation,
	useDeleteDealNoteMutation,
	useListDealNotesQuery,
} from '@/api'
import { extractErrorMessage } from '@/lib/error-message'
import { DEFAULT_LOCALE } from '@/types'

interface DealNotesSectionProps {
	dealId: string
	adding?: boolean
	onAddingChange?: (v: boolean) => void
}

function formatDateTime(iso: string): string {
	return new Date(iso).toLocaleString(DEFAULT_LOCALE, {
		day:    '2-digit',
		month:  '2-digit',
		year:   '2-digit',
		hour:   '2-digit',
		minute: '2-digit',
	})
}

export function DealNotesSection({ dealId, adding: addingProp, onAddingChange }: DealNotesSectionProps) {
	const { t } = useTranslation()
	const { data, isLoading } = useListDealNotesQuery(dealId)
	const [createNote, { isLoading: isCreating }] = useCreateDealNoteMutation()
	const [deleteNote] = useDeleteDealNoteMutation()

	const [addingLocal, setAddingLocal] = useState(false)
	const adding    = addingProp    ?? addingLocal
	const setAdding = onAddingChange ?? setAddingLocal
	const [text, setText]     = useState('')
	const [actionError, setActionError] = useState<{ title: string; description?: string } | null>(null)
	const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null)

	const notes = data?.items ?? []

	const reset = () => {
		setAdding(false)
		setText('')
	}

	async function handleSubmit() {
		if (!text.trim()) return
		setActionError(null)
		try {
			await createNote({ dealId, body: { text: text.trim() } }).unwrap()
			reset()
		} catch (err) {
			setActionError({ title: t('deals.notes.errors.create'), description: extractErrorMessage(err) })
		}
	}

	async function handleDelete(noteId: string) {
		setActionError(null)
		try {
			await deleteNote({ dealId, noteId }).unwrap()
		} catch (err) {
			setActionError({ title: t('deals.notes.errors.delete'), description: extractErrorMessage(err) })
		}
	}

	function confirmDelete() {
		if (deleteTargetId) void handleDelete(deleteTargetId)
		setDeleteTargetId(null)
	}

	if (isLoading) {
		return <Spinner size="sm" />
	}

	return (
		<div className="flex flex-col gap-3">
			{actionError && (
				<Alert variant="destructive">
					<AlertCircleIcon className="size-4" />
					<AlertTitle>{actionError.title}</AlertTitle>
					{actionError.description && <AlertDescription>{actionError.description}</AlertDescription>}
				</Alert>
			)}
			{notes.length === 0 && !adding && (
				<p className="text-sm text-(--color-muted-foreground)">{t('deals.notes.empty')}</p>
			)}

			{notes.map((note) => (
				<div
					key={note.id}
					className="flex items-start gap-3 rounded-md border border-(--color-border) bg-(--color-background) p-3"
				>
					<div className="flex flex-1 min-w-0 flex-col gap-1">
						<p className="text-sm whitespace-pre-wrap text-(--color-foreground)">{note.text}</p>
						<span className="text-xs text-(--color-muted-foreground)">
							{note.authorName ? `${note.authorName} • ` : ''}{formatDateTime(note.createdAt)}
						</span>
					</div>
					<IconButton
						variant="ghost"
						size="sm"
						type="button"
						onClick={() => setDeleteTargetId(note.id)}
						aria-label={t('common.delete')}
					>
						<Trash2Icon className="size-4" />
					</IconButton>
				</div>
			))}

			<ConfirmDialog
				open={!!deleteTargetId}
				onOpenChange={(open) => { if (!open) setDeleteTargetId(null) }}
				title={t('deals.notes.deleteDialog.title')}
				description={t('deals.notes.deleteDialog.description')}
				confirmLabel={t('common.delete')}
				cancelLabel={t('common.cancel')}
				variant="destructive"
				onConfirm={confirmDelete}
			/>

			{adding ? (
				<div className="flex flex-col gap-2 rounded-md border border-(--color-border) bg-(--color-background) p-3">
					<Textarea
						value={text}
						onChange={(e) => setText(e.target.value)}
						placeholder={t('deals.notes.placeholder')}
						className="min-h-20"
						autoFocus
					/>
					<div className="flex gap-2 justify-end">
						<Button variant="secondary" size="sm" onClick={reset}>
							{t('deals.notes.cancel')}
						</Button>
						<Button
							size="sm"
							disabled={isCreating || !text.trim()}
							onClick={() => void handleSubmit()}
						>
							{t('deals.notes.submit')}
						</Button>
					</div>
				</div>
			) : (
				<Button
					variant="outline"
					size="sm"
					iconLeft={<PlusIcon className="size-4" />}
					className="self-start"
					onClick={() => setAdding(true)}
				>
					{t('deals.notes.add')}
				</Button>
			)}
		</div>
	)
}
